export const CLAIM_STATES = {
  // Intake and Awaiting Med. Recs. follow the Client Onboarding board (Nic
  // 2026-10-09): nobody moves a claim between them, or back into them from
  // In Process or later, by hand. In Process is the floor; a claim that
  // needs onboarding again is Sent back to Onboarding. Dead -> Intake
  // reopens Client Onboarding.
  // Out of onboarding only through CM Assignment (Nic 2026-10-13), so by
  // hand Intake, Awaiting Med. Recs. and Dead offer no move past onboarding.
  intake: {
    canMoveTo: ['dead'],
  },
  'awaiting medical records': {
    canMoveTo: ['dead'],
  },
  'in process': {
    canMoveTo: ['awaiting internal review', 'dead'],
    helpMessage:
      'You must move to "Awaiting Internal Review" from here to continue in the pipeline.',
  },
  'awaiting internal review': {
    canMoveTo: [
      'in process',
      'internal review approved',
      'dead',
    ],
  },
  'internal review approved': {
    canMoveTo: [
      'in process',
      'awaiting internal review',
      'submitted to va',
      'dead',
    ],
    // Permission required to move TO this state
    requiredPermission: 'claims:approve_for_va',
    helpMessage:
      'Can only be moved to this status by authorized users. This step is required before going to "Submitted to VA".',
  },
  'submitted to va': {
    canMoveTo: [
      'in process',
      'awaiting internal review',
      'dead',
    ],
    // Permission required to move TO this state
    requiredPermission: 'claims:submit_to_va',
  },
  // Billing lanes (Invoiced / Payment Plan / Paid): moving a claim into any of
  // them is Finance-only (billing:manage). Mirrors the server rule.
  invoiced: {
    canMoveTo: ['payment plan', 'paid', 'dispute hold'],
    requiredPermission: 'billing:manage',
  },
  'payment plan': {
    canMoveTo: ['paid', 'dispute hold'],
    requiredPermission: 'billing:manage',
  },
  paid: {
    canMoveTo: ['payment plan', 'invoiced', 'dispute hold'],
    requiredPermission: 'billing:manage',
  },
  // Mirrors server/lib/claimWorkflowStatesManager.js — keep in sync.
  'dispute hold': {
    canMoveTo: ['invoiced', 'paid', 'dead'],
    // Permission required to move TO this state
    requiredPermission: 'billing:manage',
    // Permission required to move OUT of this state: releasing the billing
    // freeze stays with Finance, not general claims staff.
    exitPermission: 'billing:manage',
    helpMessage:
      'Freezes all billing activity and notifications for this claim while a payment dispute is resolved.',
  },
  dead: {
    canMoveTo: ['intake'],
  },
};

/**
 * Get available status transitions based on current status and user permissions
 * @param {string} status - Current claim status
 * @param {string[]} userPermissions - Array of user's permission keys
 * @returns {string[]} - Array of statuses the user can transition to
 */
export const availableTransitions = (status, userPermissions = []) => {
  if (!status || !CLAIM_STATES[status]) {
    throw new Error(`Invalid status provided: ${status}`);
  }

  // Override permission allows moving to any status
  if (userPermissions.includes('claims:override_status')) {
    return Object.keys(CLAIM_STATES);
  }

  const state = CLAIM_STATES[status];

  // A state can lock its EXIT behind a permission (dispute hold: releasing
  // the billing freeze is Finance's call). No permission = no moves offered.
  if (
    state.exitPermission &&
    !userPermissions.includes(state.exitPermission)
  ) {
    return [];
  }

  const canMoveTo = state.canMoveTo.filter((s) => {
    const toState = CLAIM_STATES[s];

    // Check if target state requires a specific permission
    if (toState.requiredPermission) {
      return userPermissions.includes(toState.requiredPermission);
    }

    return true;
  });

  return canMoveTo;
};
